import { useConvex } from "convex/react";
import { useCallback, useEffect, useRef } from "react";
import { ComponentProperties } from "@/components/skin/ComponentProperties";
import { useToast } from "@/hooks/use-toast";
import { useEditorStore } from "@/store/editorStore";
import { useAuth } from "@/hooks/useAuth";
import { isElectron, updateProject } from "@/data";
import { api } from "../../convex/_generated/api";

interface SkinComponent {
  id: string;
  type: "box" | "text" | "image" | "icon" | "collection-list" | "tabs";
  name: string;
  position: {
    left?: number | string;
    right?: number | string;
    top?: number | string;
    bottom?: number | string;
  };
  size: { width: number | "auto"; height: number | "auto"; aspectRatio?: string };
  style: Record<string, unknown>;
  content: Record<string, unknown>;
  visible: boolean;
  parentId?: string;
  zIndex: number;
  pointerEvents?: boolean;
  hideOnStart?: boolean;
}

// Default export for backward compatibility - now handled by persistent viewer layout
const Skin = () => null;
export default Skin;

export const SkinPanel = () => {
  const convex = useConvex();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();

  // Get state from editor store (already loaded by ProjectCraftspace)
  const {
    project,
    skinComponents,
    updateSkinComponent,
    addSkinComponentWithData,
    isDirty,
    setIsDirty,
    isSaving,
    setIsSaving,
    setProject,
  } = useEditorStore();
  const projectBackend = useEditorStore((state) => state.projectBackend);
  const copiedComponentRef = useRef<string | null>(null);

  // Get the selected component from ProjectCraftspace's state
  const selectedComponent = useEditorStore((state) => state.selectedSkinComponent);

  // When selecting a tab content in the tree, activate its parent tab
  useEffect(() => {
    if (!selectedComponent?.parentId) return;
    const parent = skinComponents.find((c) => c.id === selectedComponent.parentId);
    if (!parent || parent.type !== "tabs") return;
    const tabs = Array.isArray(parent.content?.tabs)
      ? (parent.content.tabs as Array<{ id: string }>)
      : [];
    const isTabContent = tabs.some((tab) => tab.id === selectedComponent.id);
    if (!isTabContent) return;
    if (parent.content?.activeTabId === selectedComponent.id) return;
    updateSkinComponent(parent.id, {
      content: { ...parent.content, activeTabId: selectedComponent.id },
    });
  }, [selectedComponent, skinComponents, updateSkinComponent]);

  // Keep tabs content in sync with existing tab content boxes (supports undo)
  useEffect(() => {
    const tabParents = skinComponents.filter((c) => c.type === "tabs");
    if (tabParents.length === 0) return;

    tabParents.forEach((parent) => {
      const tabs = Array.isArray(parent.content?.tabs)
        ? (parent.content.tabs as Array<{ id: string; label: string }>)
        : [];
      const childIds = new Set(
        skinComponents.filter((c) => c.parentId === parent.id).map((c) => c.id),
      );
      const nextTabs = tabs.filter((tab) => childIds.has(tab.id));
      if (nextTabs.length === tabs.length) return;
      const nextActive =
        childIds.has(parent.content?.activeTabId as string)
          ? (parent.content?.activeTabId as string)
          : nextTabs[0]?.id || "";
      updateSkinComponent(parent.id, {
        content: { ...parent.content, tabs: nextTabs, activeTabId: nextActive },
      });
    });
  }, [skinComponents, updateSkinComponent]);

  // Auto-save function
  const autoSave = useCallback(async () => {
    if (!project || isSaving) return;
    if (projectBackend !== "local" && !isAuthenticated) {
      toast({
        variant: "destructive",
        title: "Not signed in",
        description: "Sign in to save skin component changes.",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Safely handle project.data which can be Json | null
      const currentData =
        project.data && typeof project.data === "object" && project.data !== null
          ? (project.data as Record<string, unknown>)
          : {};

      const nextData = { ...currentData, skinComponents };

      if (projectBackend === "local") {
        await updateProject({
          id: project.id,
          craftspace_id: project.craftspace_id,
          data: nextData,
        });
      } else {
        await convex.mutation(api.projects.update, {
          id: project.id,
          data: nextData,
        });
      }

      setProject({ ...project, data: nextData });

      setIsDirty(false);
      // Auto-save completed silently
    } catch (err) {
      console.error("Unexpected error saving project:", err);
      // Only show error toast for critical failures
      if (err instanceof Error && err.message.includes("network")) {
        toast({
          variant: "destructive",
          title: "Auto-save failed",
          description: "Network error - check your connection.",
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    project,
    projectBackend,
    isSaving,
    isAuthenticated,
    convex,
    skinComponents,
    setIsDirty,
    setIsSaving,
    setProject,
    toast,
  ]);

  // Auto-save when components change (with debounce)
  useEffect(() => {
    if (!isDirty || isSaving) return;
    const timeoutId = setTimeout(() => {
      autoSave();
    }, 1000); // 1 second debounce

    return () => clearTimeout(timeoutId);
  }, [isDirty, isSaving, autoSave]);

  const getNextComponentName = useCallback((name: string, siblings: SkinComponent[]) => {
    const baseNameMatch = name.match(/^(.*?)(?:\s(\d+))?$/);
    const baseName = baseNameMatch?.[1] ? baseNameMatch[1] : name;
    const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const numberPattern = new RegExp(`^${escapeRegExp(baseName)}\\s(\\d+)$`);
    const existingNumbers = siblings
      .map((sibling) => sibling.name.match(numberPattern))
      .filter((match): match is RegExpMatchArray => !!match)
      .map((match) => Number.parseInt(match[1], 10))
      .filter((value) => Number.isFinite(value));
    const baseExists = siblings.some((sibling) => sibling.name === baseName);
    const maxNumber = Math.max(0, baseExists ? 1 : 0, ...existingNumbers);
    const nextNumber = Math.max(2, maxNumber + 1);
    return `${baseName} ${nextNumber}`;
  }, []);

  const insertComponentAtIndex = useCallback(
    (
      component: SkinComponent,
      parentId: string | null,
      insertIndex: number,
      overrides: Partial<SkinComponent>,
    ) => {
      const siblingComponents = skinComponents
        .filter((c) => (c.parentId ?? null) === parentId)
        .sort((a, b) => b.zIndex - a.zIndex);
      const nextName = getNextComponentName(component.name, siblingComponents);
      const nextComponent: SkinComponent = {
        ...component,
        ...overrides,
        id: crypto.randomUUID(),
        name: nextName,
        parentId: parentId ?? undefined,
        zIndex: component.zIndex,
      };
      const reordered = [...siblingComponents];
      const safeIndex = Math.min(Math.max(insertIndex, 0), reordered.length);
      reordered.splice(safeIndex, 0, nextComponent);
      reordered.forEach((sibling, index) => {
        const nextZ = reordered.length - index;
        if (sibling.id === nextComponent.id) {
          nextComponent.zIndex = nextZ;
          return;
        }
        if (sibling.zIndex !== nextZ) {
          updateSkinComponent(sibling.id, { zIndex: nextZ });
        }
      });
      return nextComponent;
    },
    [getNextComponentName, skinComponents, updateSkinComponent],
  );

  const cloneComponentTree = useCallback(
    (
      component: SkinComponent,
      parentId: string | null,
      insertIndex: number,
      rootOverrides: Partial<SkinComponent>,
    ) => {
      const rootClone = insertComponentAtIndex(component, parentId, insertIndex, rootOverrides);
      const clones: SkinComponent[] = [rootClone];

      const cloneChildren = (sourceParentId: string, nextParentId: string) => {
        const children = skinComponents
          .filter((c) => c.parentId === sourceParentId)
          .sort((a, b) => b.zIndex - a.zIndex);
        if (children.length === 0) return;

        children.forEach((child, index) => {
          const childClone: SkinComponent = {
            ...child,
            id: crypto.randomUUID(),
            parentId: nextParentId,
            zIndex: children.length - index,
          };
          clones.push(childClone);
          cloneChildren(child.id, childClone.id);
        });
      };

      cloneChildren(component.id, rootClone.id);
      return { rootClone, clones };
    },
    [insertComponentAtIndex, skinComponents],
  );

  const handleDuplicateComponent = useCallback(
    (component: SkinComponent) => {
      try {
        const parentId = component.parentId ?? null;
        const siblingComponents = skinComponents
          .filter((c) => (c.parentId ?? null) === parentId)
          .sort((a, b) => b.zIndex - a.zIndex);
        const originalIndex = siblingComponents.findIndex((c) => c.id === component.id);

        const { rootClone, clones } = cloneComponentTree(component, parentId, originalIndex + 1, {
          position: {
            ...component.position,
            left:
              typeof component.position.left === "number"
                ? component.position.left + 20
                : component.position.left,
            top:
              typeof component.position.top === "number"
                ? component.position.top + 20
                : component.position.top,
          },
        });

        clones
          .filter((clone) => clone.id !== rootClone.id)
          .forEach((clone) => addSkinComponentWithData(clone));
        addSkinComponentWithData(rootClone);
      } catch (error) {
        console.error("Error duplicating component:", error);
        toast({
          variant: "destructive",
          title: "Duplication Failed",
          description: "Failed to duplicate the component",
        });
      }
    },
    [skinComponents, addSkinComponentWithData, cloneComponentTree, toast],
  );

  useEffect(() => {
    const handleCopyEvent = (e: CustomEvent) => {
      if (e.detail?.component) {
        copiedComponentRef.current = (e.detail.component as SkinComponent).id;
      }
    };
    const handlePasteEvent = (e: CustomEvent) => {
      const sourceId = copiedComponentRef.current;
      const source = sourceId ? skinComponents.find((c) => c.id === sourceId) : null;
      if (!source) return;
      const target = e.detail?.target as SkinComponent | undefined;
      const targetIsContainer =
        target?.type === "box" || target?.type === "collection-list";
      const parentId = targetIsContainer ? target.id : target?.parentId ?? null;
      const siblingCount = skinComponents.filter((c) => (c.parentId ?? null) === parentId).length;
      const { rootClone, clones } = cloneComponentTree(source, parentId, siblingCount, {
        position: {},
      });
      clones
        .filter((clone) => clone.id !== rootClone.id)
        .forEach((clone) => addSkinComponentWithData(clone));
      addSkinComponentWithData(rootClone);
    };

    document.addEventListener("copySkinComponent", handleCopyEvent as EventListener);
    document.addEventListener("pasteSkinComponent", handlePasteEvent as EventListener);
    return () => {
      document.removeEventListener("copySkinComponent", handleCopyEvent as EventListener);
      document.removeEventListener("pasteSkinComponent", handlePasteEvent as EventListener);
    };
  }, [cloneComponentTree, addSkinComponentWithData, skinComponents]);

  // Listen for duplicate component events from keyboard shortcuts
  useEffect(() => {
    const handleDuplicateEvent = (e: CustomEvent) => {
      if (e.detail?.component) {
        handleDuplicateComponent(e.detail.component);
      }
    };

    document.addEventListener("duplicateComponent", handleDuplicateEvent as EventListener);
    return () =>
      document.removeEventListener("duplicateComponent", handleDuplicateEvent as EventListener);
  }, [handleDuplicateComponent]);

  const handleUpdateComponent = (componentId: string, newData: Partial<SkinComponent>) => {
    try {
      const currentComponent = skinComponents.find((component) => component.id === componentId);
      if (!currentComponent) {
        updateSkinComponent(componentId, newData);
        return;
      }

      let normalizedUpdate: Partial<SkinComponent> = { ...newData };

      // If user manually renames in the tree, keep that choice as the permanent override.
      if (typeof newData.name === "string") {
        normalizedUpdate = {
          ...normalizedUpdate,
          content: {
            ...(currentComponent.content || {}),
            ...(newData.content || {}),
            __manualName: true,
          },
        };
      }

      // Auto-name text components from text content unless a manual rename exists.
      const incomingContent = newData.content as Record<string, unknown> | undefined;
      const hasIncomingText = incomingContent && Object.prototype.hasOwnProperty.call(incomingContent, "text");
      if (currentComponent.type === "text" && hasIncomingText && typeof newData.name !== "string") {
        const mergedContent = {
          ...(currentComponent.content || {}),
          ...(incomingContent || {}),
        };
        const currentTextValue =
          typeof currentComponent.content?.text === "string" ? currentComponent.content.text.trim() : "";
        const currentName = currentComponent.name?.trim() || "";
        const hasManualOverride = mergedContent.__manualName === true;
        const isDefaultTextName = /^Text(?:\s\d+)?$/i.test(currentName);
        const isCurrentlyAutoNamed =
          !currentName || isDefaultTextName || (!!currentTextValue && currentName === currentTextValue);

        const shouldAutoName = !hasManualOverride && isCurrentlyAutoNamed;
        const nextTextValue = typeof mergedContent.text === "string" ? mergedContent.text.trim() : "";

        normalizedUpdate = {
          ...normalizedUpdate,
          content: mergedContent,
          ...(shouldAutoName && nextTextValue ? { name: nextTextValue } : {}),
          ...(!shouldAutoName ? { content: { ...mergedContent, __manualName: true } } : {}),
        };
      }

      updateSkinComponent(componentId, normalizedUpdate);

      // Component update completed silently
    } catch (error) {
      console.error("Error updating component:", error);
      // Only show error toast for critical failures
      if (error instanceof Error && error.message.includes("network")) {
        toast({
          variant: "destructive",
          title: "Update failed",
          description: "Network error - check your connection.",
        });
      }
    }
  };

  const _handleLinkPanorama = (componentId: string, panoramaId: string) => {
    try {
      const currentComponent = skinComponents.find((comp) => comp.id === componentId);
      updateSkinComponent(componentId, {
        content: {
          ...(currentComponent?.content || {}),
          linkedPanoramaId: panoramaId,
        },
      });

      toast({
        title: "Panorama Linked",
        description: "Component now opens the linked panorama when clicked",
      });
    } catch (error) {
      console.error("Error linking panorama:", error);
      toast({
        variant: "destructive",
        title: "Linking Failed",
        description: "Failed to link the panorama",
      });
    }
  };

  // Only render the properties panel (ComponentLibrary is in left panel)
  return (
    <div className="h-full bg-gray-800">
      <ComponentProperties
        selectedComponent={selectedComponent}
        onUpdateComponent={handleUpdateComponent}
        onDuplicateComponent={handleDuplicateComponent}
        components={skinComponents}
      />
    </div>
  );
};
